import React, { useState, useEffect } from 'react';
import { Calendar, TrendingUp, AlertTriangle, CheckCircle, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

const LifecycleManagement = () => {
  const [lifecycleData, setLifecycleData] = useState(null);
  const [productsData, setProductsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [lifecycleRes, productsRes] = await Promise.all([
        fetch(`${API_URL}/api/trm/stats/lifecycle`),
        fetch(`${API_URL}/api/trm/products`)
      ]);
      
      if (!lifecycleRes.ok || !productsRes.ok) throw new Error('Failed to fetch data');
      
      const lifecycle = await lifecycleRes.json();
      const products = await productsRes.json();
      
      setLifecycleData(lifecycle);
      setProductsData(products);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading lifecycle data...</div>;
  if (error) return <div style={{ padding: '40px', color: 'var(--cares-orange)' }}>Error: {error}</div>;
  if (!lifecycleData || !productsData) return null;

  const products = productsData.products || [];
  const activeProducts = products.filter(p => p.record_status === 'Active');
  const retiredProducts = products.filter(p => p.record_status === 'Retired');

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--cares-text-2)', marginBottom: '8px' }}>
          Product Lifecycle Management
        </h1>
        <p style={{ fontSize: '14px', color: 'var(--cares-text-label)' }}>
          Track product lifecycles, identify EOL risks, and plan transitions
        </p>
      </div>

      {/* Lifecycle Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '32px' }}>
        {lifecycleData.map((stage, idx) => {
          const Icon = stage.lifecycle_stage === 'Active' ? CheckCircle : AlertTriangle;
          const color = stage.lifecycle_stage === 'Active' ? '#10b981' : '#f59e0b';
          
          return (
            <div key={idx} style={{
              background: 'var(--cares-surface-card)',
              padding: '24px',
              borderRadius: '12px',
              border: `2px solid ${color}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <Icon size={24} color={color} />
                <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>
                  {stage.lifecycle_stage}
                </span>
              </div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: 'var(--cares-text-2)', marginBottom: '8px' }}>
                {stage.version_count}
              </div>
              <div style={{ fontSize: '14px', color: 'var(--cares-text-label)' }}>
                versions ({stage.percentage}%)
              </div>
              <div style={{ marginTop: '12px', height: '4px', background: 'var(--cares-surface-board)', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ width: `${stage.percentage}%`, height: '100%', background: color, transition: 'width 0.5s' }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Product Lists by Status */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: '24px' }}>
        {/* Active Products */}
        <div style={{ background: 'var(--cares-surface-card)', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid var(--cares-line-card)' }}>
            <CheckCircle size={24} color="var(--cares-green)" />
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--cares-text-2)' }}>
              Active Products ({activeProducts.length})
            </h2>
          </div>
          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            {activeProducts.slice(0, 20).map((product) => (
              <div key={product.product_id} style={{
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '8px',
                background: 'var(--cares-surface-board)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--cares-text-2)' }}>
                    {product.canonical_name}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--cares-text-label)', marginTop: '4px' }}>
                    {product.manufacturer_name} • {product.trm_category}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--cares-green)' }}>
                    {product.version_count} versions
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Retired Products */}
        <div style={{ background: 'var(--cares-surface-card)', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid var(--cares-line-card)' }}>
            <AlertTriangle size={24} color="var(--cares-amber)" />
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--cares-text-2)' }}>
              Retired Products ({retiredProducts.length})
            </h2>
          </div>
          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            {retiredProducts.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', color: 'var(--cares-text-label)' }}>
                No retired products found
              </div>
            ) : (
              retiredProducts.map((product) => (
                <div key={product.product_id} style={{
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '8px',
                  background: 'color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card))',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--cares-text-2)' }}>
                      {product.canonical_name}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--cares-text-label)', marginTop: '4px' }}>
                      {product.manufacturer_name} • {product.trm_category}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LifecycleManagement;
